<template>
    <v-card>
        <v-toolbar dense flat>
            <v-toolbar-title>
                <v-icon :color="toolbarColor">mdi-database-search</v-icon> Database Queries
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <!-- Add Query -->
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn ref="addBtn" v-bind="attrs" v-on="on" icon @click="addQuery()">
                        <v-icon :color="primaryColor">mdi-database-plus</v-icon>
                    </v-btn>
                </template>
                <span>Add Query</span>
            </v-tooltip>
            <!-- Reset Query -->
            <v-dialog v-model="resetDialog" width="540">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn ref="resetBtn" v-bind="attrs" v-on="on" :color="warningColor" icon @click="openResetDialog">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-database-refresh</v-icon>
                            </template>
                            <span>Reset Query</span>
                        </v-tooltip>
                    </v-btn>
                </template>
                <v-card>
                    <v-card-title>
                        Reset Confirmation
                    </v-card-title>
                    <v-card-text>
                        Are you sure you want to <b>reset</b> this query? This action cannot be undone! Please click
                        confirm to
                        proceed.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="resetConfirmBtn" :color="successColor" text @click="resetQuery">
                            Confirm
                        </v-btn>
                        <v-btn :color="warningColor" text @click="closeResetDialog">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <!-- Download Query -->
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn ref="downloadBtn" v-bind="attrs" v-on="on" :disabled="!runQueryID" icon
                        @click="downloadQuery">
                        <v-icon :color="infoColor">mdi-file-download</v-icon>
                    </v-btn>
                </template>
                <span>Download Query</span>
            </v-tooltip>
            <!-- Load Query -->
            <v-dialog v-model="loadDialog" width="540">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn ref="loadBtn" v-bind="attrs" v-on="on" :color="infoColor" icon @click="openLoadDialog">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-database-arrow-up</v-icon>
                            </template>
                            <span>Load A Query</span>
                        </v-tooltip>
                    </v-btn>
                </template>
                <v-card ref="loadQueryCard">
                    <v-card-title>
                        Load A Query
                    </v-card-title>
                    <v-card-text>
                        <v-form ref="loadForm" v-model="loadValid" lazy-validation>
                            <v-select v-model="queryLoadID" dense :items="loadQueryItems" item-text="name"
                                item-value="id" label="Saved Database Queries" outlined
                                placeholder="Please select a database query configuration.." :rules="queryLoadRules"
                                @change="resolveQuery">
                            </v-select>
                        </v-form>
                        <v-row>
                            <v-col cols="4" class="font-weight-bold py-1 text-right">
                                Created By
                            </v-col>
                            <v-col cols="8" class="py-1">
                                {{ queryLoad.createdByUsername || 'N/A'}}
                            </v-col>
                            <v-col cols="4" class="font-weight-bold py-1 text-right">
                                Date Created
                            </v-col>
                            <v-col cols="8" class="py-1">
                                {{ queryLoad.createdTimestamp ? new Date(queryLoad.createdTimestamp * 1000).toLocaleString() : 'N/A'}}
                            </v-col>
                            <v-col cols="4" class="font-weight-bold py-1 text-right">
                                Name
                            </v-col>
                            <v-col cols="8" class="py-1">
                                {{ queryLoad.name || 'N/A'}}
                            </v-col>
                            <v-col cols="4" class="font-weight-bold py-1 text-right">
                                Description
                            </v-col>
                            <v-col cols="8" class="py-1">
                                {{ queryLoad.description || 'N/A'}}
                            </v-col>
                            <v-col cols="4" class="font-weight-bold py-1 text-right">
                                Database Type
                            </v-col>
                            <v-col cols="8" class="py-1">
                                {{ queryLoad.databaseType || 'N/A'}}
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="loadConfirmBtn" :color="successColor" :disabled="!loadValid" text @click="setQuery">
                            Load
                        </v-btn>
                        <v-btn :color="warningColor" text @click="closeLoadDialog">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <!-- Save Query -->
            <v-dialog v-model="saveDialog" width="540">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn ref="saveBtn" v-bind="attrs" v-on="on" :color="successColor" icon @click="openSaveDialog">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-database-check</v-icon>
                            </template>
                            <span>Save A New Query</span>
                        </v-tooltip>
                    </v-btn>
                </template>
                <v-card ref="saveQueryCard">
                    <v-card-title>
                        Save A New Query
                    </v-card-title>
                    <v-card-text>
                        <v-form ref="saveForm" v-model="saveValid" lazy-validation>
                            <v-text-field v-model="querySave.name" dense label="Name" name="name" outlined
                                :rules="nameRules" required></v-text-field>
                            <v-textarea v-model="querySave.description" counter hide-details
                                label="Description (not required)" name="description" outlined></v-textarea>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="saveConfirmBtn" :color="successColor" :disabled="!saveValid" text
                            @click="saveQuery">
                            Save
                        </v-btn>
                        <v-btn :color="warningColor" text @click="closeSaveDialog">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <!-- Update Query -->
            <v-dialog v-model="updateDialog" width="540">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn ref="updateBtn" v-bind="attrs" v-on="on" :color="successColor" :disabled="validQuery" icon
                        @click="openUpdateDialog">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-database-edit</v-icon>
                            </template>
                            <span>Update Query</span>
                        </v-tooltip>
                    </v-btn>
                </template>
                <v-card>
                    <v-card-title>
                        Update Query
                    </v-card-title>
                    <v-card-text>
                        <v-form ref="updateForm" v-model="updateValid" lazy-validation>
                            <v-text-field v-model="queryUpdate.name" label="Name" name="name" outlined
                                :rules="nameRules" required></v-text-field>
                            <v-textarea v-model="queryUpdate.description" counter label="Description (not required)"
                                name="description" outlined></v-textarea>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="updateConfirmBtn" :color="successColor" :disabled="!updateValid" text
                            @click="updateQuery">
                            Update
                        </v-btn>
                        <v-btn :color="warningColor" text @click="closeUpdateDialog">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <!-- Delete Query -->
            <v-dialog v-model="deleteDialog" width="540">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn ref="deleteBtn" v-bind="attrs" v-on="on" :color="dangerColor" :disabled="validQuery" icon
                        @click="openDeleteDialog">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-database-remove</v-icon>
                            </template>
                            <span>Delete Query</span>
                        </v-tooltip>
                    </v-btn>
                </template>
                <v-card>
                    <v-card-title>
                        Delete Confirmation
                    </v-card-title>
                    <v-card-text>
                        Are you sure you want to <b>delete</b> this query? This action cannot be undone! Please click
                        confirm to
                        proceed.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="deleteConfirmBtn" :color="successColor" text @click="deleteQuery">
                            Confirm
                        </v-btn>
                        <v-btn :color="warningColor" text @click="closeDeleteDialog">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-toolbar>
    </v-card>
</template>

<script>
export default {
    name: 'reports-menu',
    props: {
        currQuery: {
            required: true,
            type: Object
        },
        loadQueryItems: {
            required: true,
            type: Array
        },
        statusSave: {
            required: true,
            type: Boolean
        },
        statusUpdate: {
            required: true,
            type: Boolean
        },
        runQueryID: {
            required: true,
            type: String
        }
    },
    data() {
        return {
            deleteDialog: false,
            loadDialog: false,
            loadValid: true,
            nameRules: [
                v => !!v || 'Name is required',
                v => (v && v.length >= 2) || 'Name must be at least 2 characters',
            ],
            queryLoad: {},
            queryLoadID: '',
            queryLoadRules: [
                v => !!v || 'Database query configuration is required',
            ],
            querySave: {
                description: '',
                name: ''
            },
            queryUpdate: {
                description: '',
                name: ''
            },
            resetDialog: false,
            saveDialog: false,
            saveValid: true,
            updateDialog: false,
            updateValid: true,
        }
    },
    methods: {
        addQuery() {
            this.$emit('addQuery');
        },
        closeDeleteDialog() {
            this.deleteDialog = false;
        },
        closeLoadDialog() {
            this.loadDialog = false;
        },
        closeResetDialog() {
            this.resetDialog = false;
        },
        closeSaveDialog() {
            this.saveDialog = false;
        },
        closeUpdateDialog() {
            this.updateDialog = false;
        },
        deleteQuery() {
            this.$refs.deleteConfirmBtn.loading = true;
            this.$emit('deleteQuery');
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$refs.deleteConfirmBtn.loading = false;
                    this.closeDeleteDialog();
                }, 1000);
            });
        },
        downloadQuery() {
            this.$emit('downloadQuery');
        },
        loadQuery() {
            this.$refs.loadQueryCard.disabled = true;
            this.$refs.loadQueryCard.loading = true;
            this.$emit('loadQuery');
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$refs.loadQueryCard.disabled = false;
                    this.$refs.loadQueryCard.loading = false;
                }, 1500);
            });
        },
        openDeleteDialog() {
            this.deleteDialog = true;
        },
        openLoadDialog() {
            this.queryLoad = {};
            this.queryLoadID = '';

            if (this.$refs.loadForm) {
                this.$refs.loadForm.resetValidation();
            }

            this.loadDialog = true;
            setTimeout(() => {
                this.loadQuery();
            }, 250);
        },
        openResetDialog() {
            this.resetDialog = true;
        },
        openSaveDialog() {
            this.querySave.description = '';
            this.querySave.name = '';

            if (this.$refs.saveForm) {
                this.$refs.saveForm.resetValidation();
            }

            this.saveDialog = true;
        },
        openUpdateDialog() {
            this.queryUpdate = {
                ...this.currQuery
            };
            this.updateDialog = true;
        },
        resolveQuery() {
            for (let i = 0; i < this.loadQueryItems.length; i++) {
                if (this.loadQueryItems[i].id == this.queryLoadID) {
                    this.queryLoad = {
                        ...this.loadQueryItems[i]
                    };
                }
            }
        },
        resetQuery() {
            this.$refs.resetConfirmBtn.loading = true;
            this.$emit('resetQuery');
            this.closeResetDialog();
        },
        saveQuery() {
            if (this.$refs.saveForm.validate()) {
                this.$refs.saveConfirmBtn.loading = true;
                this.$emit('saveQuery', this.querySave);

                setTimeout(() => {
                    if (this.statusSave) {
                        this.$nextTick(() => {
                            this.$refs.saveConfirmBtn.loading = false;
                            this.$emit('setStatusSave', false);
                            this.closeSaveDialog();
                        });
                    } else {
                        this.$refs.saveConfirmBtn.loading = false;
                    }
                }, 1000);
            }
        },
        setQuery() {
            if (this.$refs.loadForm.validate()) {
                this.$refs.loadConfirmBtn.loading = true;
                this.$emit('setQuery', this.queryLoad);
                this.$nextTick(() => {
                    this.$refs.loadConfirmBtn.loading = false;
                    this.closeLoadDialog();
                });
            }
        },
        updateQuery() {
            if (this.$refs.updateForm.validate()) {
                this.$refs.updateConfirmBtn.loading = true;
                this.$emit('updateQuery', this.queryUpdate);

                setTimeout(() => {
                    if (this.statusUpdate) {
                        this.$nextTick(() => {
                            this.$refs.updateConfirmBtn.loading = false;
                            this.$emit('setStatusUpdate', false);
                            this.closeUpdateDialog();
                        });
                    } else {
                        this.$refs.updateConfirmBtn.loading = false;
                    }
                }, 1000);
            }
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        infoColor() {
            return '#5578eb';
        },
        primaryColor() {
            return '#5867dd';
        },
        secondaryColor() {
            return '#f9f9fc';
        },
        successColor() {
            return '#1dc9b7';
        },
        toolbarColor() {
            return '#8950FC';
        },
        validQuery() {
            if (this.currQuery && this.currQuery.id) {
                return false;
            }

            return true;
        },
        warningColor() {
            return '#ffb822'
        }
    }
}
</script>